import React from 'react';
import { useTranslation } from '../../i18n';
import './Skeleton.css';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ width = '100%', height = 14, radius = 6, className = '' }) => (
  <span
    className={`skeleton ${className}`.trim()}
    style={{ width, height, borderRadius: radius }}
    aria-hidden="true"
  />
);

const SkeletonRegion: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => {
  const { t } = useTranslation();
  return (
    <div className={`skeleton-region ${className}`} role="status" aria-busy="true" aria-live="polite">
      <span className="skeleton-sr-only">{t('common.loading')}</span>
      {children}
    </div>
  );
};

export const GoalCardsSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <SkeletonRegion className="skeleton-goal-grid">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="skeleton-card">
        <div className="skeleton-row">
          <Skeleton width="60%" height={18} />
          <Skeleton width={72} height={22} radius={8} />
        </div>
        <Skeleton width="35%" height={12} />
        <Skeleton width="90%" />
        <Skeleton width="70%" />
        <div className="skeleton-row skeleton-row--end">
          <Skeleton width="40%" height={12} />
          <Skeleton width={36} height={12} />
        </div>
        <Skeleton height={8} radius={999} />
      </div>
    ))}
  </SkeletonRegion>
);

export const TodoRowsSkeleton: React.FC<{ count?: number; withHeader?: boolean }> = ({ count = 5, withHeader = true }) => (
  <SkeletonRegion className="skeleton-todo-list">
    {withHeader && (
      <div className="skeleton-row skeleton-page-header">
        <Skeleton width={160} height={26} />
        <Skeleton width={96} height={26} radius={999} />
      </div>
    )}
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="skeleton-todo-row">
        <Skeleton width={56} height={22} radius={6} />
        <div className="skeleton-todo-row__content">
          <Skeleton width="45%" height={11} />
          <Skeleton width={`${60 + ((i * 13) % 30)}%`} height={16} />
        </div>
        <Skeleton width={88} height={36} radius={8} />
      </div>
    ))}
  </SkeletonRegion>
);

export const GoalDetailSkeleton: React.FC = () => (
  <SkeletonRegion className="skeleton-goal-detail">
    <div className="skeleton-card skeleton-hero">
      <Skeleton width={120} height={14} />
      <div className="skeleton-row">
        <Skeleton width="55%" height={30} />
        <Skeleton width={120} height={40} radius={8} />
      </div>
      <Skeleton width="80%" />
      <div className="skeleton-meta">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="skeleton-meta__card">
            <Skeleton width="50%" height={11} />
            <Skeleton width="40%" height={22} />
          </div>
        ))}
      </div>
    </div>
    {Array.from({ length: 3 }, (_, i) => (
      <div key={i} className="skeleton-card">
        <div className="skeleton-row">
          <Skeleton width="50%" height={16} />
          <Skeleton width={110} height={36} radius={8} />
        </div>
        <Skeleton height={8} radius={999} />
      </div>
    ))}
  </SkeletonRegion>
);

export default Skeleton;
